import { reactive, ref } from "vue";
import { defineStore } from "pinia";
import type { LoadingStatus } from "./loading_status";
import StorageBackendService from "@/core/services/StorageBackendService";
import type { Association, AssociationEdgeDevice } from "./association";

export interface StorageProvider {
  id: number
  name: string
  logo_path: string
  service_name: string
  short_name: string
  cloud_platform_url: string
}

export type Continent =
  | 'europe'
  | 'north-america'
  | 'south-america'
  | 'asia'
  | 'australia'
  | 'middle-east'
  | 'africa'

export interface StorageLocationCarbonIntensityDetails {
  carbon_intensity_year: number // which year the carbon intensity values are from
  grid_carbon_intensity: number // gCO2eq/kWh
  pue: number
  carbon_free_energy_pct: number
  effective_carbon_intensity: number // gCO2eq/kWh
  market_adjusted_carbon_intensity: number // gCO2eq/kWh
  pue_source: string
  pue_comment: string | null
  carbon_free_energy_source: string
  carbon_free_energy_comment: string | null
}

export interface StorageLocation {
  id: number
  name: string // Human-readable name
  provider_id: number
  provider?: StorageProvider
  continent: Continent
  countrycode: string | null
  country: string | null
  lat: number
  lng: number
  orig_region: string
  distance_km?: number
  storage_cost?: number // $/TB/month
  egress_cost?: number // $/TB
  carbon_intensity_details?: StorageLocationCarbonIntensityDetails
}

export const format_storage_location_name = (
  location: StorageLocation,
  short: boolean = false,
  include_provider: boolean = false
): string => {
  const country = location.country?.replace('United States', 'US')
  const country_matches_location = country?.trim().toLowerCase() === location.name.trim().toLowerCase()
  const location_name = short || !country || country_matches_location ? location.name : `${location.name}, ${country}`

  if (!include_provider) return location_name

  return [location.provider?.name, location_name].filter(Boolean).join(' ')
}

export type FlyRegionLocation = {
  city: string
  country: string
  countrycode: string
  continent: Continent
  lat: number
  lng: number
  flag_char: string
}

export const fly_region_locations: Record<string, FlyRegionLocation> = {
  ams: { city: 'Amsterdam', country: 'Netherlands', countrycode: 'netherlands', continent: 'europe', lat: 52.3676, lng: 4.9041, flag_char: '🇳🇱' },
  arn: { city: 'Stockholm', country: 'Sweden', countrycode: 'sweden', continent: 'europe', lat: 59.3293, lng: 18.0686, flag_char: '🇸🇪' },
  bom: { city: 'Mumbai', country: 'India', countrycode: 'india', continent: 'asia', lat: 19.0760, lng: 72.8777, flag_char: '🇮🇳' },
  cdg: { city: 'Paris', country: 'France', countrycode: 'france', continent: 'europe', lat: 48.8566, lng: 2.3522, flag_char: '🇫🇷' },
  dfw: { city: 'Dallas, Texas', country: 'United States', countrycode: 'us', continent: 'north-america', lat: 32.7767, lng: -96.7970, flag_char: '🇺🇸' },
  ewr: { city: 'Secaucus, New Jersey', country: 'United States', countrycode: 'us', continent: 'north-america', lat: 40.7895, lng: -74.0565, flag_char: '🇺🇸' },
  fra: { city: 'Frankfurt', country: 'Germany', countrycode: 'germany', continent: 'europe', lat: 50.1109, lng: 8.6821, flag_char: '🇩🇪' },
  gru: { city: 'Sao Paulo', country: 'Brazil', countrycode: 'brazil', continent: 'south-america', lat: -23.5505, lng: -46.6333, flag_char: '🇧🇷' },
  iad: { city: 'Ashburn, Virginia', country: 'United States', countrycode: 'us', continent: 'north-america', lat: 39.0438, lng: -77.4874, flag_char: '🇺🇸' },
  jnb: { city: 'Johannesburg', country: 'South Africa', countrycode: 'south-africa', continent: 'africa', lat: -26.2041, lng: 28.0473, flag_char: '🇿🇦' },
  lax: { city: 'Los Angeles, California', country: 'United States', countrycode: 'us', continent: 'north-america', lat: 34.0522, lng: -118.2437, flag_char: '🇺🇸' },
  lhr: { city: 'London', country: 'United Kingdom', countrycode: 'uk', continent: 'europe', lat: 51.5074, lng: -0.1278, flag_char: '🇬🇧' },
  nrt: { city: 'Tokyo', country: 'Japan', countrycode: 'japan', continent: 'asia', lat: 35.6762, lng: 139.6503, flag_char: '🇯🇵' },
  ord: { city: 'Chicago, Illinois', country: 'United States', countrycode: 'us', continent: 'north-america', lat: 41.8781, lng: -87.6298, flag_char: '🇺🇸' },
  sin: { city: 'Singapore', country: 'Singapore', countrycode: 'singapore', continent: 'asia', lat: 1.3521, lng: 103.8198, flag_char: '🇸🇬' },
  sjc: { city: 'San Jose, California', country: 'United States', countrycode: 'us', continent: 'north-america', lat: 37.3382, lng: -121.8863, flag_char: '🇺🇸' },
  syd: { city: 'Sydney', country: 'Australia', countrycode: 'australia', continent: 'australia', lat: -33.8688, lng: 151.2093, flag_char: '🇦🇺' },
  yyz: { city: 'Toronto', country: 'Canada', countrycode: 'canada', continent: 'north-america', lat: 43.6532, lng: -79.3832, flag_char: '🇨🇦' },
}

export interface EdgeDevice {
  id: number
  name: string
  description?: string
  storage_url: string
  region: string
}

export interface StorageBucket {
  id: number
  backend_id: number
}

// All locations (cloud+edge) with sort info and whether it's deleted/added
export interface LocationWithTypeAndSort {
  type: 'cloud' | 'edge',
  cloud_loc?: StorageLocation
  edge_loc?: AssociationEdgeDevice
  edge_association?: Association
  sort_order: number,
  deleted: boolean
  added: boolean
}


export const useStorageLocationStore = defineStore("storage-location", () => {
  const locations = reactive<StorageLocation[]>([]);
  const edge_devices = reactive<EdgeDevice[]>([]);
  const locations_loading = reactive<LoadingStatus>({loading: false})

  const cloud_providers = reactive<StorageProvider[]>([])
  const cloud_providers_loading = reactive<LoadingStatus>({loading: false})

  const team_buckets = reactive<StorageBucket[]>([]);
  const team_buckets_loading = reactive<LoadingStatus>({loading: false})

  async function load_team_buckets(): Promise<null> {
    /**
     * Loads the storage buckets of the logged in user's team.
     * Team Buckets consists of the the buckets themselves and the
     * associated StorageProvider
     */
    if(team_buckets_loading.loading) return null

    team_buckets_loading.loading = true
    return StorageBackendService.get_team_buckets()
      .then(({data}) => {
        team_buckets.splice(0)
        team_buckets.push(...data)
        return null
      })
      .finally(() => team_buckets_loading.loading = false)
  }

  async function load_cloud_providers(): Promise<null> {
    if(cloud_providers_loading.loading) return null


    cloud_providers_loading.loading = true
    return StorageBackendService.list_cloud_providers()
      .then(({data}) => {
          // Replace cloud providers
          cloud_providers.splice(0)
          cloud_providers.push(...data)
        })
      .finally(() => cloud_providers_loading.loading = false)
  }

  async function load_locations(): Promise<string | null> {
    if(locations_loading.loading) return null
    const cloud_locations_tmp = reactive<StorageLocation[]>([]);
    const backends_with_distance = true
    const backends_with_pricing = true
    const with_carbon_intensity = true
    const promises = [
      // Load cloud backends and store them temporarily in cloud_locations_tmp
      StorageBackendService.list_s3_backends(backends_with_distance, backends_with_pricing, with_carbon_intensity).then(({data}) => { cloud_locations_tmp.push(...data) })
    ]
    if(!cloud_providers.length){
      // Also load cloud providers if they have not been loaded yet
      promises.push(load_cloud_providers())
    }

    locations_loading.loading = true
    return Promise.all(promises).then(_ => {
      // Add cloud provider to storage locations
      cloud_locations_tmp.forEach(loc => loc.provider = cloud_providers.find(p => p.id == loc.provider_id))

      const locations_without_provider = cloud_locations_tmp.filter(l => !l.provider)
      if(locations_without_provider.length){
        console.error("Backends without cloud provider:", locations_without_provider)
      }

      // Replace locations
      locations.splice(0)
      locations.push(...cloud_locations_tmp)
      return null
    }).catch(({response}) => {
      console.error(`Error loading storage locations: HTTP ${response.status}`, response.body)
      return "Error loading storage locations"
    }).finally(() => {
      locations_loading.loading = false
    })
  }

  function get_location_name(backend_id: number) {
    const loc = locations.find(l => l.id == backend_id)
    if(!loc) return null

    return format_storage_location_name(loc, true, true)
  }

  return {
    locations,
    edge_devices,
    cloud_providers,
    team_buckets,
    load_locations,
    load_cloud_providers,
    load_team_buckets,
    locations_loading,
    cloud_providers_loading,
    team_buckets_loading,
    get_location_name
  };
});
